import { Star, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router';

import { useFavorites, useRemoveFavorite } from '@/features/favorites/api';
import { cn } from '@/lib/utils';

/**
 * G-01 — the slim bar under the top bar: the person's pinned screens as
 * links, in their order, each with a small × to unpin. Absent — not empty —
 * when there is nothing pinned, so a person who never uses it never sees it.
 * Logical offsets only (`ms-`, `px-`), so it mirrors under dir="rtl".
 */
export function FavoritesBar() {
  const { t } = useTranslation();
  const { data } = useFavorites();
  const remove = useRemoveFavorite();

  if (data === undefined || data.favorites.length === 0) return null;

  return (
    <nav
      aria-label={t('fav.bar')}
      className="flex h-9 shrink-0 items-center gap-2 overflow-x-auto border-b border-border bg-card px-6"
    >
      <Star className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
      <ul className="flex items-center gap-1">
        {data.favorites.map((favorite) => (
          <li key={favorite.id} className="flex shrink-0 items-center rounded-md hover:bg-accent">
            <NavLink
              to={favorite.route}
              className={({ isActive }) =>
                cn(
                  'max-w-[200px] truncate px-2 py-1 text-sm transition-colors',
                  isActive ? 'font-medium text-foreground' : 'text-muted-foreground hover:text-foreground',
                )
              }
            >
              {favorite.label}
            </NavLink>
            <button
              type="button"
              onClick={() => remove.mutate(favorite.id)}
              disabled={remove.isPending}
              aria-label={t('fav.removeItem', { label: favorite.label })}
              title={t('fav.removeItem', { label: favorite.label })}
              className="me-1 flex size-6 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            >
              <X className="size-3" aria-hidden />
            </button>
          </li>
        ))}
      </ul>
    </nav>
  );
}
